export interface SimpleLocation {
  lat: number;
  lng: number;
  accuracy?: number;
}

export type GeoErrorReason =
  | 'unsupported'
  | 'insecure-context'
  | 'permission-denied'
  | 'unavailable'
  | 'timeout'
  | 'unknown';

export class GeolocationError extends Error {
  reason: GeoErrorReason;
  constructor(reason: GeoErrorReason, message: string) {
    super(message);
    this.reason = reason;
  }
}

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/**
 * Returns the browser's current geolocation permission state without prompting.
 * Useful for surfacing why a deny is happening (cached deny vs. fresh prompt vs. OS-level block).
 */
export async function queryGeolocationPermission(): Promise<PermissionState> {
  if (typeof navigator === 'undefined' || !('permissions' in navigator)) {
    return 'unsupported';
  }
  try {
    const status = await navigator.permissions.query({
      name: 'geolocation' as PermissionName,
    });
    return status.state as PermissionState;
  } catch {
    return 'unsupported';
  }
}

const GET_TIMEOUT_MS = 8_000;
const WATCH_TIMEOUT_MS = 8_000;
const RETRY_BACKOFF_MS = [0, 500, 1200];

function mapPositionError(err: GeolocationPositionError): GeolocationError {
  if (err.code === err.PERMISSION_DENIED) {
    return new GeolocationError(
      'permission-denied',
      'Location permission is blocked for this site.',
    );
  }
  if (err.code === err.POSITION_UNAVAILABLE) {
    return new GeolocationError(
      'unavailable',
      'Your location is unavailable. Try moving to an open area or enabling system location services.',
    );
  }
  if (err.code === err.TIMEOUT) {
    return new GeolocationError(
      'timeout',
      'Location request timed out. Please try again.',
    );
  }
  return new GeolocationError(
    'unknown',
    err.message || 'Could not get your location.',
  );
}

function getOnce(maximumAge: number): Promise<SimpleLocation> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) => reject(mapPositionError(err)),
      { enableHighAccuracy: true, timeout: GET_TIMEOUT_MS, maximumAge },
    );
  });
}

function watchFirstFix(): Promise<SimpleLocation> {
  return new Promise((resolve, reject) => {
    let resolved = false;
    let lastErr: GeolocationError | null = null;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (resolved) return;
        resolved = true;
        navigator.geolocation.clearWatch(watchId);
        clearTimeout(timer);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        lastErr = mapPositionError(err);
      },
      { enableHighAccuracy: true, timeout: WATCH_TIMEOUT_MS, maximumAge: 0 },
    );

    const timer = setTimeout(() => {
      if (resolved) return;
      navigator.geolocation.clearWatch(watchId);
      reject(
        lastErr ??
          new GeolocationError(
            'timeout',
            'Location request timed out. Please try again.',
          ),
      );
    }, WATCH_TIMEOUT_MS);
  });
}

/**
 * Robust location fetcher that handles Chrome + macOS Core Location quirks:
 *   1. Up to 3 getCurrentPosition attempts with backoff.
 *   2. If still failing while the Permissions API reports `granted`, fall back
 *      to watchPosition, which subscribes to a position stream and is more
 *      reliable on stuck Core Location states.
 */
export async function getCurrentLocation(): Promise<SimpleLocation> {
  if (!('geolocation' in navigator)) {
    throw new GeolocationError(
      'unsupported',
      'Your browser does not support location services.',
    );
  }

  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    throw new GeolocationError(
      'insecure-context',
      'Geolocation requires HTTPS or localhost. The current site is served over an insecure connection.',
    );
  }

  const permState = await queryGeolocationPermission();
  let lastErr: GeolocationError | null = null;

  for (let attempt = 0; attempt < RETRY_BACKOFF_MS.length; attempt++) {
    if (RETRY_BACKOFF_MS[attempt] > 0) {
      await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt]));
    }
    try {
      return await getOnce(attempt === 0 ? 30_000 : 0);
    } catch (err) {
      lastErr = err as GeolocationError;
      // Real deny (permission API agrees) — stop retrying.
      if (lastErr.reason === 'permission-denied' && permState !== 'granted') {
        throw lastErr;
      }
    }
  }

  if (permState === 'granted') {
    try {
      return await watchFirstFix();
    } catch (err) {
      lastErr = err as GeolocationError;
    }
  }

  throw lastErr ?? new GeolocationError('unknown', 'Could not get your location.');
}
